import React from 'react';
import LatestSdk from '../welcome/LatestSdk';
import AddProperty from '../configs/cells/controls/AddProperty';
import NavLink from '../routing/NavLink';
import useKiponosNavigator from '../../hooks/useKiponosNavigator';
import CopyToClip from '../../infra/CopyToClip';
import useLatestSdk from '../../hooks/useLatestSdk';
import './MultiPageDocs.css';

const MultiPageDocs = () => {
  const {PageKeys} = useKiponosNavigator();
  const {group, artifact, version, loading, fullName} = useLatestSdk();
  
  const gradleCode = `implementation '${loading ? 'Getting it...' : fullName}'`;
  const mavenCode = `<dependency>
  <groupId>${loading ? 'Getting it...' : group}</groupId>
  <artifactId>${loading ? 'Getting it...' : artifact}</artifactId>
  <version>${loading ? 'Getting it...' : version}</version>
</dependency>
`;
  
  //const sdkCode = `Kiponos kiponos = Kiponos.createForCurrentTeam();`

  return (
    <div className='multiPageDocs'>
      <section className="guide-card">
        <h1>Welcome to Kiponos</h1>
        <hr/>
        <p>
          Kiponos is a real-time configuration platform letting you manage application settings dynamically.
        </p>
        <p>
          Your application reacts instantly to any config item you modify online in your account.
        </p>
        <p>
          <strong>Let's get you started quickly!</strong>
        </p>
        </section>

        <section className='guide-card'>
          <h2>Step 1: Explore your default Envionment</h2>
          <hr/>
          <div className='docs-note-flow'>
            Once you've signed-up 
            <div className='docs-nav'>
              <NavLink pageKey={PageKeys.SIGNUP} /> 
            </div>
            and logged-in 
            <div className='docs-nav'>
              <NavLink pageKey={PageKeys.LOGIN} />
            </div>
          </div>
          <p>
            We've already created for you a default "dev" environment with a default "base" config profile.<br/>
            Follow the Config Wizard to select it, making it the currently active config set (config tree) to work with.
          </p>
          <p>
            Besides the default "dev" environment and its "base" config set, we'll see how your application can seamlessly work with differnet environments and various config sets<br/>
            Like "Local-Dev", "Dev-Branch-A", "Staging", "Staging-MyFeature-1", "CICD-Integration-Tests", "Production", etc.<br/>
            <br/>
            All without changing your application code<br/>
          </p>
        </section>

        <section className='guide-card'>
          <h2>Step 2: Create your first Config Item</h2>
          <p>Let's start with our default env (and its default "base" config set)</p>
          <hr/>
          <ol>
            <li>Click the Add Item button, which looks like this: 
              <span style={{display:'flex', maxWidth: 80}}><AddProperty /></span>
            </li>
            <li>Kiponos auto focus on a new input field - the "new-programming-key"</li>
            <li>A Config Item has KEY and VALUE, and here you define the "KEY" part - that's the name you will use in your application code to get the value.</li>
            <li>Name it based on your programming logic like <code>db-port</code>, <code>max-retries</code>, <code>max-queue-size</code>, <code>health-url</code>, etc.</li>
            <li>Press [Enter] or click outside the input field and the key is automatically saved.</li>
            <li>The "Value" field shows. Enter the item's value like (like <code>8080</code>, <code>/health/state</code>, etc). Similarly, the value is automatically saved.</li>            
            <li>Congratulations! You just created your first config item in Kiponos.</li>
            <li>Now you can use this key in your application code to get whatever value is currently entered!</li>
          </ol>
            <div className='docs-note-nice'>
              <strong>Note:</strong> 
              There is no need to "Save" anything in Kiponos.<br/>Any change you do is instantly saved and dispatched in real-time!<br/>
              For example, everyone in your team instantly see the new config key you've just created and at that point anyone in your team may enter their suggestion for a value.
              That's real-time collaboration. (permissions and roles later on...)<br/>
            </div>
        </section>

        <section className='guide-card'>
          <h1>Connecting Your App</h1>
          <p>
            Now that you have a config item, let's see how you can use it in your Java application.
          </p>
          <hr/>
          <h3>What you'll need:</h3>

          <ol>
            <li>The Kiponos Java SDK. (Publicly available in Maven Central Repository)</li>
            <li>SDK Access Tokens. (Available online in your account)</li>
            <li>(Optional - A config path - only if you have renamed the default "base")</li>
          </ol>
        </section>

        <section className='guide-card'>          
          <h2>Step 3: Include the Kiponos SDK in your build</h2>
          <p>
            Kiponos SDK is publicly available in Maven Central Repository.<br/>
            (Latest is: <LatestSdk />)
          </p>
          <div>
            <div className='titleWithCopy'>
              In Gradle: <CopyToClip text={gradleCode} key='gradle'/>
            </div>            
            <code>
              {gradleCode}
            </code>
          </div>
          <p/>          
          <div>
            <div className='titleWithCopy'>
              In Maven: <CopyToClip text={mavenCode} key='maven'/>
            </div>
            <code>
              <pre>
                {mavenCode}
              </pre>
            </code>
          </div>

          <p>
            More info at <a href='https://central.sonatype.com/artifact/io.kiponos/sdk-boot-3' target='_blank' rel='noopener noreferrer'>Maven Central</a>.
          </p>
          </section>

          <section className='guide-card'>
          <h2>Step 4: Get The SDK Tokens</h2>
          <p>
            Kiponos links your application to your config data securely using encrypted keys (tokens).<br/>
            Each environment has a pair of unique tokens: Id Token and Access Token<br/>
            To generate them, click "Connect Your App" on the Side Bar.<br/>
            Then click on the "Generate dev SDK Tokens" button.<br/>
          </p>
          <div>
            Conveniently, copy each token value using its "Copy" button next to it.<br/>
            And define the two environment variables in your application environment accordingly:<br/>
            <br/>
            <code>KIPONOS_ID</code> and <code>KIPONOS_ACCESS</code><br/>
            <br/>
            <strong>Note:</strong> These tokens are unique to your environment and should not be shared publicly.<br/>
              They are used to authenticate your application on that specific environment with Kiponos Hub and access your config data securely.<br/>
            <br/>
            <div className='docs-note-nice'>
              <strong>Note:</strong> If you are running your application in a local environment,
            you can set these environment variables in your IDE or terminal before running your application.<br/>          
            Copy the tokens using the convenient "Copy" button next to each token<br/>
            </div>
          </div>
          <div className='docs-note-nice'>
            Having the keys at the environment level allows you to reuse the same tokens for multiple applications running in the same environment.
          </div>
          </section>

          <section className='guide-card'>
          <h2>Step 5: Choose a Config Profile in your environment to work with</h2>
          <hr/>
          <p>
            Kiponos allows you to define multiple config profiles in each environment.<br/>
            For example, running your application on "Staging" environment will use by default the "base" config profile.<br/>
            However to run the integration tests on the same "Staging" env, you'll want a differnet config profile - with config values relevant for the tests.<br/>
          </p>
          <div>            
            That's why the SDK accepts an optional third parameter: a JVM System property `-Dkipoonos` which provides the config profile name to use.<br/>
            If not provided, the SDK uses the default "base" config profile of the environment.<br/>
            <br/>            
            <div className='docs-note-nice'>
              <strong>Note:</strong>
              Kiponos allows you to modify any config item in your config profile without redeploying any files and without even restarting your application.<br/>
              Your application reacts instantly - in real-time - to any config change while it is running.<br/>
            </div>
          </div>

          <div>
            To tell Kiponos which config profile to use, you set its unique path in a JVM System Property: <br/>
            <code>-Dkiponos=your-config-profile-path</code>
            <br/>
            <p/>
            The path value is uniquely identified by:<br/>
            Application, Release, Environment and Config Profile name.<br/>
            For example, if your application is called "MedicalServer", release "v3.2.0", in "dev" environment, and you want to use a config profile you named "SSL", then the path will be:<br/>
            <code>['MedicalServer']['v3.2.0']['dev']['SSL']</code>
            <br/>
            Therefore, the JVM System Property will be:<br/>
            <code>-Dkiponos="['MedicalServer']['v3.2.0']['dev']['SSL']"</code><br/>
            <br/>
            The "Connect Your App" page in Kiponos Web, provides you with the exact path value to use for your application.<br/>
            So you don't have to remember the exact format. Just know that you can provide it to uniquely choose your config profile<br/>
            <div className='docs-note-nice'>
              <strong>Note:</strong>
              Using a full path is safer as you are clearly "stating" what path you want your application to work with. However, we're considering a balanced, short and unique "Code Name" for each config profile.<br/>
              This will be available in the future, so you can use a short name like "SSL" instead of the full path.<br/>
            </div>
            <div className='docs-note-nice'>
              The path is case-sensitive and should match the config path names exactly.<br/>
            </div>
            <br/>
          </div>
        </section>      

        <section className="guide-card">
          <h2>Step 6: Use the SDK to access Your Config Data</h2>
            Creating the SDK instance automatically connects to the Kiponos Server. (and automatically reconnects when necessary)<br/>
            You don't need to provide any URL or endpoint in your code as everything the SDK needs is already provided by the encrypted tokens.<br/>
            <br/>
            The API let you access your config items by their key, which returns their current value in real-time<br/>
            <br/>
            The following code example assumes there is a config item with the key <code>max-username-length</code> and another with the key <code>is-ready-api</code>.<br/>
            <br/>            
            <pre className='code-example'>
            {`
                    Kiponos kiponos = Kiponos.createForCurrentTeam();
                    
                    // returns 24
                    int maxUsernmaeLength = kiponos.getInt("max-username-length");

                    // returns "/health/state"
                    String isReadyAPI = kiponos.get("is-ready-api")

            `}</pre>          
          
            <div className='docs-note-nice'>
              <strong>Note:</strong>              
                The SDK has a permanent WebSocket connection which allows it to always listen for real-time updates without any performance cost!
                Any change you do in the Kiponos Web UI is instantly dispatched to the local memory of the SDK.<br/>
                Therefore, the SDK API always get you the latest value of any config item without any network call or delay.<br/>
                The latest values are always ready in the SDK memory, so you can access them instantly.<br/>
            </div>

      </section>

      <section className="guide-card">
        <h1>Powerful Use Cases</h1>
        <ul>
          <li>
            Change log levels in production without restarting your server.
          </li>
          <li>
            Switch feature flags instantly for A/B testing.
          </li>
          <li>
            Update third-party credentials in real-time when rotating secrets.
          </li>
        </ul>
        <p>
          All these happen live, with no need to redeploy or restart any application.
        </p>

        <div className='docs-note-nice'>
            Kiponos provides role-based access control, so you can control who can edit the config items and who can only read them or even see them at all.
            <br/>
            If your plan supports multiple teams, you can define roles per team! Any user can be a member of multiple teams, with different roles and permissions in each team.
        </div>
      </section>
    
      <section className="guide-card">
        <h1>Next Steps</h1>
        <p>
          Now that you have a basic understanding of how to use Kiponos, you can explore more advanced features:
        </p>
        <ul>
          <li>
            Invite User - Invite your team members to collaborate on your config items.
          </li>
          <li>
            My Profile - Update your profile and contact details. In Kiponos, you can change your email. it is not tied to your login, so you can change it anytime.
          </li>
          <li>
            Task Manager - Use the Task Manager to manage your development tasks and collaborate with your team.
          </li>
        </ul>            
      </section>
    </div>
  );
};

export default MultiPageDocs;